import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Save, User, Building, Mail, Shield, Wrench, Megaphone, Sparkles, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import RefreshStatus from '../components/RefreshStatus';

const Settings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const [userProfile, setUserProfile] = useState({
    name: '',
    email: '',
    role: 'user',
    organization_name: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [backfillStatus, setBackfillStatus] = useState('idle'); // idle | confirming | running | done
  const [backfillResult, setBackfillResult] = useState(null);
  const [sentimentStatus, setSentimentStatus] = useState('idle');
  const [sentimentResult, setSentimentResult] = useState(null);
  const [failures, setFailures] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchFailures();
  }, []);

  const fetchFailures = async () => {
    try {
      const resp = await api.get('/admin/recent-failures?hours=24');
      setFailures(resp.data);
    } catch (err) {
      // Non-fatal — just don't render the section
      setFailures({ failures: [], count: 0, error: true });
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/profile');
      setUserProfile({
        name: response.data.profile.name || '',
        email: user?.email || '',
        role: response.data.profile.role || 'user',
        organization_name: response.data.organization?.name || ''
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await api.put('/auth/profile', {
        name: userProfile.name
      });
      setSuccess('Profile updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setUserProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBackfillMedia = async () => {
    setBackfillStatus('running');
    setError('');
    try {
      const response = await api.post('/admin/backfill-media-classification', {});
      setBackfillResult(response.data);
      setBackfillStatus('done');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start backfill');
      setBackfillStatus('idle');
    }
  };

  const handleBackfillSentiment = async () => {
    setSentimentStatus('running');
    setError('');
    try {
      const response = await api.post('/admin/backfill-sentiment', {});
      setSentimentResult(response.data);
      setSentimentStatus('done');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start sentiment backfill');
      setSentimentStatus('idle');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setChangingPassword(true);
    setError('');
    setSuccess('');

    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      setChangingPassword(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      setChangingPassword(false);
      return;
    }

    try {
      await api.post('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setSuccess('Password changed successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-2">
          Manage your account settings and preferences
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-500 bg-green-50 text-green-900">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {['owner', 'admin'].includes(userProfile.role) && <RefreshStatus />}

      {/* Profile Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profile Information
          </CardTitle>
          <CardDescription>
            Update your personal information and display name
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={userProfile.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter your full name"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={userProfile.email}
                  disabled
                  className="bg-muted cursor-not-allowed"
                />
                <p className="text-xs text-muted-foreground">
                  Email cannot be changed. Contact support if you need to update your email.
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="role">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Role
                  </div>
                </Label>
                <Input
                  id="role"
                  type="text"
                  value={userProfile.role}
                  disabled
                  className="bg-muted cursor-not-allowed capitalize"
                />
                <p className="text-xs text-muted-foreground">
                  Role is managed by your organization administrator
                </p>
              </div>
            </div>

            <Button type="submit" disabled={saving} className="w-full sm:w-auto">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Organization Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Organization
          </CardTitle>
          <CardDescription>
            Your organization details
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="organization">Organization Name</Label>
              <Input
                id="organization"
                type="text"
                value={userProfile.organization_name}
                disabled
                className="bg-muted cursor-not-allowed"
              />
              <p className="text-xs text-muted-foreground">
                Contact your organization administrator to change organization details
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent failures */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Recent run failures
            {failures && failures.count > 0 && (
              <span className="text-xs font-normal text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">
                {failures.count} in last 24h
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Keyword runs that failed in the last 24 hours, usually from OpenAI quota or rate limits.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!failures ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : failures.count === 0 ? (
            <p className="text-sm text-emerald-700">All runs successful in the last 24 hours.</p>
          ) : (
            <div className="space-y-2">
              {failures.failures.slice(0, 10).map(f => (
                <div key={f.id} className="flex items-start justify-between gap-3 p-3 rounded-md border bg-orange-50/50">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{f.keyword}</p>
                    <p className="text-xs text-muted-foreground">
                      {f.website?.brandName ? `${f.website.brandName} · ` : ''}
                      {new Date(f.createdAt).toLocaleString()}
                    </p>
                    {f.error && (
                      <p className="text-xs text-orange-800 mt-1 line-clamp-2 font-mono">
                        {f.error}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              {failures.count > 10 && (
                <p className="text-xs text-muted-foreground italic">
                  + {failures.count - 10} more
                </p>
              )}
              <p className="text-xs text-muted-foreground pt-2">
                If you're seeing recurring failures, check your OpenAI billing dashboard for quota or rate-limit issues.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Maintenance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Maintenance
          </CardTitle>
          <CardDescription>
            One-off data jobs you can run against your organization's citations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Megaphone className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />
              <div className="flex-1">
                <h3 className="font-medium">Backfill media-outlet classification</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Classifies older citations that were stored before media tagging was wired in. Each unique domain triggers one GPT call (cached), so cost is bounded by domain count, not citation count. Used to populate the Opportunities page's media table.
                </p>
              </div>
            </div>

            {backfillStatus === 'idle' && (
              <Button variant="outline" onClick={() => setBackfillStatus('confirming')}>
                Run backfill
              </Button>
            )}

            {backfillStatus === 'confirming' && (
              <Alert>
                <AlertDescription>
                  <p className="mb-3">
                    This kicks off a background job that calls GPT once per unique unclassified domain. Cost is small but non-zero. Continue?
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleBackfillMedia}>
                      Yes, run it
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setBackfillStatus('idle')}>
                      Cancel
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {backfillStatus === 'running' && (
              <Button variant="outline" disabled>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting…
              </Button>
            )}

            {backfillStatus === 'done' && backfillResult && (
              <Alert className="border-emerald-500 bg-emerald-50 text-emerald-900">
                <AlertDescription>
                  <p className="font-medium mb-1">Backfill started</p>
                  <p className="text-sm">
                    Classifying {backfillResult.total} citation{backfillResult.total === 1 ? '' : 's'} in the background. Refresh the Opportunities page in a few minutes — it'll populate as domains are classified.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3"
                    onClick={() => { setBackfillStatus('idle'); setBackfillResult(null); }}
                  >
                    Dismiss
                  </Button>
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div className="space-y-3 pt-6 border-t">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />
              <div className="flex-1">
                <h3 className="font-medium">Backfill brand sentiment</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Classifies how each existing citation frames your brand (or topic) as positive / neutral / negative. One gpt-4o-mini call per citation — cheap but per-row, so cost scales with citation count rather than domain count. Powers the Brand Sentiment card on the dashboard.
                </p>
              </div>
            </div>

            {sentimentStatus === 'idle' && (
              <Button variant="outline" onClick={() => setSentimentStatus('confirming')}>
                Run sentiment backfill
              </Button>
            )}

            {sentimentStatus === 'confirming' && (
              <Alert>
                <AlertDescription>
                  <p className="mb-3">
                    This kicks off a background job that classifies the sentiment of every unclassified citation. Cost scales with citation count (one mini call each). Continue?
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleBackfillSentiment}>
                      Yes, run it
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setSentimentStatus('idle')}>
                      Cancel
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {sentimentStatus === 'running' && (
              <Button variant="outline" disabled>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting…
              </Button>
            )}

            {sentimentStatus === 'done' && sentimentResult && (
              <Alert className="border-emerald-500 bg-emerald-50 text-emerald-900">
                <AlertDescription>
                  <p className="font-medium mb-1">Sentiment backfill started</p>
                  <p className="text-sm">
                    Classifying {sentimentResult.total} citation{sentimentResult.total === 1 ? '' : 's'} in the background. Refresh the dashboard in a few minutes — the Brand Sentiment card will populate as the job progresses.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3"
                    onClick={() => { setSentimentStatus('idle'); setSentimentResult(null); }}
                  >
                    Dismiss
                  </Button>
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Account Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Account Security
          </CardTitle>
          <CardDescription>
            Change your password and manage security settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  placeholder="Enter your current password"
                  disabled={changingPassword}
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Enter your new password"
                  disabled={changingPassword}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Password must be at least 6 characters long
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm your new password"
                  disabled={changingPassword}
                  required
                />
              </div>
            </div>

            <Button type="submit" disabled={changingPassword} className="w-full sm:w-auto">
              {changingPassword ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Changing Password...
                </>
              ) : (
                <>
                  <Shield className="mr-2 h-4 w-4" />
                  Change Password
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;